import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { PASSWORD_MIN_LENGTH } from '@proctoring/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { homePathForRole } from '../auth/guards.js';

/**
 * Consumes the single-use invite token from the email link and sets the first password.
 *
 * The API answers with a full login response, so a successful setup signs the user
 * straight in rather than bouncing them back to /login to retype what they just chose.
 */
export function SetupPasswordPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const { setupPassword } = useAuth();
  const navigate = useNavigate();

  const inviteToken = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!inviteToken) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-lg font-semibold text-session-text">Invalid invitation link</h1>
          <p className="mt-2 text-sm text-session-muted">
            This link is missing its token. Open the link from your invitation email again, or ask
            for a new one.
          </p>
          <Link to="/login" className="btn-secondary mt-5 inline-flex">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    // Both rules are re-checked server-side; validating here only avoids burning the
    // single-use token on a typo the user can still see and fix.
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirmation) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const user = await setupPassword({ inviteToken, password });
      navigate(homePathForRole(user.role), { replace: true });
    } catch (cause) {
      setError(messageForError(cause));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <h1 className="text-lg font-semibold tracking-tight text-session-text">
          Set your password
        </h1>
        <p className="mt-1 mb-6 text-sm text-session-muted">
          Choose a password of at least {PASSWORD_MIN_LENGTH} characters. This link can only be used
          once.
        </p>

        <form onSubmit={onSubmit} className="panel space-y-4 p-5" noValidate>
          {error ? (
            <p role="alert" className="form-error">
              {error}
            </p>
          ) : null}

          <div>
            <label htmlFor="password" className="field-label">
              New password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="confirmation" className="field-label">
              Confirm password
            </label>
            <input
              id="confirmation"
              name="confirmation"
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              className="field-input"
            />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Saving…' : 'Set password and continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
